/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import {
  Container, Typography, Box, Grid, TextField, InputAdornment, Button,
  CircularProgress, Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Link as RouterLink } from 'react-router-dom';
import { doc, getDocFromServer } from 'firebase/firestore';
import { db } from '../../firebase';
import { poojas as defaultPoojas } from '../../data/pooja-data';

const GOLD = '#d4af37';

const ACCENT_COLORS = [
  '#c0392b', '#2c3e50', '#1a5276', '#d35400', '#117a65', '#7d6608',
  '#8E24AA', '#1E88E5', '#43A047', '#F4511E', '#FB8C00', '#3949AB',
];

/** Splits name into Malayalam part and bracketed English transliteration */
function splitVazhipadName(name: string): { ml: string; en: string } {
  const match = name.match(/^(.*?)\s*(\(.*\))$/);
  if (!match) return { ml: name, en: '' };
  return { ml: match[1].trim(), en: match[2] };
}

export default function VazhipadList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [vazhipads, setVazhipads] = useState<Array<{ name: string; price: string }>>([]);
  const [loadingVazhipads, setLoadingVazhipads] = useState(false);

  useEffect(() => { loadVazhipads(); }, []);

  const loadVazhipads = async () => {
    try {
      setLoadingVazhipads(true);
      const docRef = doc(db, 'pooja_data', 'main');
      const docSnap = await getDocFromServer(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.poojas && data.poojas.length > 0) {
          setVazhipads(data.poojas.map((p: any) => ({ name: p.name, price: '\u20B9' + p.price })));
        } else {
          setVazhipads(defaultPoojas.map(p => ({ name: p.name, price: '\u20B9' + p.price })));
        }
      } else {
        setVazhipads(defaultPoojas.map(p => ({ name: p.name, price: '\u20B9' + p.price })));
      }
    } catch {
      setVazhipads(defaultPoojas.map(p => ({ name: p.name, price: '\u20B9' + p.price })));
    } finally {
      setLoadingVazhipads(false);
    }
  };

  const isBrahmarakshas = (n: string) =>
    n === '\u0D30\u0D15\u0D4D\u0D37\u0D38\u0D3F\u0D28\u0D41\u0D2A\u0D42\u0D1C' ||
    n === '\u0D30\u0D15\u0D4D\u0D37\u0D38\u0D3F\u0D28\u0D41 \u0D2A\u0D3E\u0D32\u0D4D\u200D\u0D2A\u0D3E\u0D2F\u0D38\u0D02' ||
    n === '\u0D30\u0D15\u0D4D\u0D37\u0D38\u0D3F\u0D28\u0D41 \u0D35\u0D3F\u0D33\u0D15\u0D4D\u0D15\u0D4D';

  const filtered = vazhipads.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const mainItems = filtered.filter(item => !isBrahmarakshas(item.name));
  const brahmaItems = filtered.filter(item => isBrahmarakshas(item.name));

  const steps = [
    'Visit the temple counter directly for instant booking',
    'Book online through our website portal',
    'Call temple office for advance booking',
    'Prasadam will be provided after the offering',
  ];

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Vazhipad List — Kottayil Temple | വഴിപാട് ലിസ്റ്റ്" description="Complete vazhipad (offerings) list with prices at Kottayil Temple (Kotta-Kovilakam Kshethram). Book vazhipadu online — Pushpanjali, Archana, Abhishekam and more." path="/poojas/vazhipad" keywords="vazhipad list, Kottayil temple vazhipadu, temple offerings, vazhipad prices, vazhipadu booking, വഴിപാട്, വഴിപാട് ലിസ്റ്റ്, കൊട്ടയിൽ ക്ഷേത്രം വഴിപാട്, അർച്ചന, pushpanjali, archana" />
      <PageBanner title="Vazhipad List" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* ══════ VIBRANT HERO ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', mb: 3.5,
              background: 'linear-gradient(135deg, #064e3b 0%, #059669 50%, #10b981 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.07, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3.5, sm: 4 }, pb: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <LocalOfferIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Chip label="Devotional Offerings" size="small" icon={<FormatListBulletedIcon sx={{ fontSize: 14, color: '#fff !important' }} />} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.7rem', mb: 1, height: 24, backdropFilter: 'blur(8px)' }} />
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.5rem', sm: '1.8rem' }, color: '#fff', lineHeight: 1.2 }}>
                      Vazhipad Offerings
                    </Typography>
                  </Box>
                  {!loadingVazhipads && (
                    <Chip
                      label={`${vazhipads.length} offerings`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 26,
                        backdropFilter: 'blur(8px)',
                      }}
                    />
                  )}
                </Box>
                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, maxWidth: 600 }}>
                  Traditional devotional offerings to the deity. Browse the complete list of vazhipad offerings
                  available at the temple and book your preferred offering.
                </Typography>
              </Box>
            </Box>

            {/* ══════ SEARCH & FILTER ══════ */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 3.5,
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #059669, #10b981)',
                px: { xs: 2.5, sm: 3 }, py: 2,
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <SearchIcon sx={{ fontSize: 20, color: '#fff' }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>
                  Search Offerings
                </Typography>
              </Box>

              <Box sx={{
                px: { xs: 2.5, sm: 3.5 }, py: 2.5,
                display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap',
              }}>
                <TextField
                  size="small"
                  placeholder="Search offerings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1, minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px', bgcolor: '#f8fafc',
                      fontSize: '0.85rem',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                      '&:hover fieldset': { borderColor: 'rgba(16,185,129,0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#10b981', borderWidth: 1.5 },
                    },
                  }}
                />
                <Button
                  onClick={loadVazhipads}
                  disabled={loadingVazhipads}
                  startIcon={loadingVazhipads ? <CircularProgress size={14} sx={{ color: '#10b981' }} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    borderRadius: '14px', px: 2, py: 0.8,
                    border: '1px solid rgba(16,185,129,0.2)',
                    color: '#059669', fontWeight: 700, fontSize: '0.78rem',
                    textTransform: 'none',
                    '&:hover': { bgcolor: 'rgba(16,185,129,0.06)' },
                  }}
                >
                  {loadingVazhipads ? 'Loading' : 'Refresh'}
                </Button>
              </Box>
            </Box>

            {/* ══════ VAZHIPAD LIST ══════ */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 3.5,
            }}>
              <Box sx={{
                px: { xs: 2.5, sm: 3.5 }, py: 2,
                borderBottom: '1px solid rgba(0,0,0,0.04)',
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <AutoAwesomeIcon sx={{ fontSize: 20, color: '#059669' }} />
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#1e293b' }}>
                  All Offerings
                </Typography>
                {searchTerm && (
                  <Chip
                    label={`${filtered.length} result${filtered.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(16,185,129,0.08)',
                      color: '#059669',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 22,
                    }}
                  />
                )}
              </Box>

              {loadingVazhipads ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CircularProgress size={32} sx={{ color: '#10b981' }} />
                  <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8', mt: 2 }}>Loading offerings...</Typography>
                </Box>
              ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <SearchIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                  <Typography sx={{ fontSize: '0.88rem', color: '#94a3b8' }}>
                    No offerings found matching "{searchTerm}"
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2 }}>
                  {/* Main items */}
                  <Grid container spacing={2}>
                    {mainItems.map((item, idx) => {
                      const accent = ACCENT_COLORS[idx % ACCENT_COLORS.length];
                      return (
                      <Grid item xs={12} sm={6} key={item.name}>
                        <Box sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          px: 2.5, py: 2, borderRadius: '18px',
                          bgcolor: '#fff',
                          border: '1px solid rgba(0,0,0,0.05)',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                          transition: 'all 320ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          '&:hover': {
                            bgcolor: '#fff',
                            transform: 'translateY(-3px)',
                            boxShadow: `0 12px 32px rgba(0,0,0,0.07), 0 0 0 1px ${accent}18`,
                            '& .idx-orb': { transform: 'scale(1.1)', boxShadow: `0 4px 14px ${accent}35` },
                            '& .accent-line': { width: '100%', opacity: 1 },
                          },
                          position: 'relative', overflow: 'hidden',
                        }}>
                          {/* Top accent line - grows on hover */}
                          <Box className="accent-line" sx={{
                            position: 'absolute', top: 0, left: 0,
                            width: '30%', height: 2, 
                            background: `linear-gradient(90deg, ${accent}, ${accent}60, transparent)`,
                            borderRadius: '0 0 4px 0',
                            transition: 'width 400ms ease, opacity 400ms ease',
                            opacity: 0.6,
                          }} />

                          {/* Index orb */}
                          <Box className="idx-orb" sx={{
                            width: 36, height: 36, borderRadius: '12px',
                            background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                            boxShadow: `0 3px 10px ${accent}25`,
                            transition: 'all 320ms ease',
                          }}>
                            <Typography className="notranslate" translate="no" sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#fff', letterSpacing: 0.5 }}>
                              {String(idx + 1).padStart(2, '0')}
                            </Typography>
                          </Box>

                          {/* Name */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {(() => { const parts = splitVazhipadName(item.name); return (<>
                              <Typography className="notranslate" lang="ml" sx={{
                                fontSize: { xs: '0.8rem', sm: '0.85rem' }, fontWeight: 700, color: '#1e293b',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                lineHeight: 1.4,
                              }}>
                                {parts.ml}
                              </Typography>
                              {parts.en && (
                                <Typography className="notranslate" translate="no" sx={{
                                  fontSize: '0.68rem', fontWeight: 500, color: '#64748b',
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                  lineHeight: 1.3, mt: 0.2,
                                }}>
                                  {parts.en}
                                </Typography>
                              )}
                            </>); })()}
                          </Box>

                          {/* Price tag */}
                          <Box className="notranslate" translate="no" sx={{
                            px: 1.5, py: 0.5,
                            borderRadius: '10px',
                            background: `linear-gradient(135deg, ${accent}08, ${accent}14)`,
                            border: `1px solid ${accent}18`,
                            flexShrink: 0,
                          }}>
                            <Typography sx={{
                              fontSize: '0.78rem', fontWeight: 800, color: accent,
                              letterSpacing: '-0.02em', lineHeight: 1,
                            }}>
                              {item.price}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      );
                    })}
                  </Grid>

                  {/* Brahmarakshas section */}
                  {brahmaItems.length > 0 && (
                    <>
                      <Box sx={{
                        mt: 4, mb: 2.5, px: 2.5, py: 2,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(5,150,105,0.03))',
                        border: '1px solid rgba(16,185,129,0.15)',
                        display: 'flex', alignItems: 'center', gap: 2,
                      }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: '10px',
                          background: 'linear-gradient(135deg, #059669, #10b981)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 3px 10px rgba(16,185,129,0.25)',
                        }}>
                          <AutoAwesomeIcon sx={{ fontSize: 16, color: '#fff' }} />
                        </Box>
                        <Typography className="notranslate" lang="ml" sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#047857' }}>
                          {'\u0D2C\u0D4D\u0D30\u0D39\u0D4D\u0D2E\u0D30\u0D15\u0D4D\u0D37\u0D38\u0D3F\u0D28\u0D41\u0D33\u0D4D\u0D33 \u0D35\u0D34\u0D3F\u0D2A\u0D3E\u0D1F\u0D41\u0D15\u0D33\u0D4D\u200D'}
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        {brahmaItems.map((item, idx) => (
                          <Grid item xs={12} sm={6} key={item.name}>
                            <Box sx={{
                              display: 'flex', alignItems: 'center', gap: 1.5,
                              px: 2.5, py: 2, borderRadius: '18px',
                              bgcolor: '#fff',
                              border: '1px solid rgba(16,185,129,0.1)',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                              transition: 'all 320ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                              '&:hover': {
                                transform: 'translateY(-3px)',
                                boxShadow: '0 12px 32px rgba(16,185,129,0.1)',
                                '& .brahma-orb': { transform: 'scale(1.1)' },
                              },
                              position: 'relative', overflow: 'hidden',
                            }}>
                              <Box className="accent-line" sx={{
                                position: 'absolute', top: 0, left: 0,
                                width: '30%', height: 2,
                                background: 'linear-gradient(90deg, #059669, #10b98160, transparent)',
                                borderRadius: '0 0 4px 0',
                                opacity: 0.6,
                              }} />
                              <Box className="brahma-orb" sx={{
                                width: 36, height: 36, borderRadius: '12px',
                                background: 'linear-gradient(135deg, #059669, #10b981)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: '0 3px 10px rgba(16,185,129,0.25)',
                                transition: 'all 320ms ease',
                              }}>
                                <AutoAwesomeIcon sx={{ fontSize: 16, color: '#fff' }} />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                {(() => { const parts = splitVazhipadName(item.name); return (<>
                                  <Typography className="notranslate" lang="ml" sx={{
                                    fontSize: { xs: '0.8rem', sm: '0.85rem' }, fontWeight: 700, color: '#1e293b',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    lineHeight: 1.4,
                                  }}>
                                    {parts.ml}
                                  </Typography>
                                  {parts.en && (
                                    <Typography className="notranslate" translate="no" sx={{
                                      fontSize: '0.68rem', fontWeight: 500, color: '#64748b',
                                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                      lineHeight: 1.3, mt: 0.2,
                                    }}>
                                      {parts.en}
                                    </Typography>
                                  )}
                                </>); })()}
                              </Box>
                              <Box className="notranslate" translate="no" sx={{
                                px: 1.5, py: 0.5,
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, rgba(5,150,105,0.06), rgba(16,185,129,0.12))',
                                border: '1px solid rgba(16,185,129,0.18)',
                                flexShrink: 0,
                              }}>
                                <Typography sx={{
                                  fontSize: '0.78rem', fontWeight: 800, color: '#059669',
                                  letterSpacing: '-0.02em', lineHeight: 1,
                                }}>
                                  {item.price}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </Box>
              )}
            </Box>

            {/* ══════ DARK SLATE FOOTER — BOOKING CTA ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
              <Box sx={{ position: 'absolute', bottom: -30, right: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: 3.5, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(212,175,55,0.3)',
                  }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>
                    How to Book Vazhipad
                  </Typography>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {steps.map((step, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{
                          width: 28, height: 28, borderRadius: '10px',
                          background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, mt: 0.2,
                          boxShadow: '0 2px 8px rgba(212,175,55,0.2)',
                        }}>
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: '#fff' }}>
                            {idx + 1}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, fontWeight: 500 }}>
                          {step}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    component={RouterLink} to="/poojas/booking"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
                    sx={{
                      background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
                      color: '#fff', fontWeight: 800,
                      px: 4, py: 1.3, borderRadius: '16px',
                      fontSize: '0.88rem', textTransform: 'none',
                      boxShadow: '0 8px 28px rgba(212,175,55,0.35)',
                      transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      '&:hover': {
                        transform: 'scale(1.03)',
                        boxShadow: '0 12px 36px rgba(212,175,55,0.45)',
                      },
                    }}
                  >
                    Book Vazhipad Online
                  </Button>
                </Box>
              </Box>
            </Box>

          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="poojas" activePath="/poojas/vazhipad" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
